import { Formik } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import callApi from '../../utils/callApi';
import { config } from '../../utils/config';
import { getInitialDynamicFieldValues } from '../../utils/dynamicFields';
import snakeCase from '../../utils/snakeCase';
import {
  pushErrorTrackingEventFromErrorObject,
  pushErrorTrackingEventFromI18nKey,
  pushTrackingEvent
} from '../../utils/tracking';
import AuthForm from '../molecules/AuthForm';
import { showDefaultError, showNotification } from '../molecules/Notifications';

export type EmbeddedAuthBaseProps = { isEmbeddedAuth?: boolean };

const EmbeddedAuth = ({
  returningCustomer,
  initialType
}: {
  returningCustomer?: { firstName: string; lastName: string; email: string };
  initialType: 'signIn' | 'register';
}) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    pushTrackingEvent({
      event: 'login_modal',
      action: 'email',
      label: null
    });
  }, []);

  const signIn = async (payload: { email: string; password: string }) => {
    const response = await callApi({ endpoint: '/customer/login', payload, flow: 'legacy' });
    pushTrackingEvent({
      event: 'login',
      content_name: document.location.pathname,
      page_type: 'checkout:modal_login',
      page_type_id: '',
      method: 'email',
      status: 'successful',
      customer_id: response.id,
      unique_customer_id: response.customData?.customer_unique_id,
      eh: response.trackingHash,
      preselected_payment_method: response.selectedPaymentMethod,
      ...(config.tracking.sendPlaintextEmail && { email: payload.email })
    });
  };

  const setPasswordForCustomerNumber = async (payload: {
    birthDate: string;
    email: string;
    password: string;
    publicKey: string;
  }) => {
    const response = await callApi({ endpoint: '/customer/authenticate', payload, flow: 'legacy' });
    pushTrackingEvent({
      event: 'set_password',
      content_name: document.location.pathname,
      page_type: 'checkout:modal_login_with_customer_id',
      page_type_id: '',
      method: 'email',
      status: 'successful',
      customer_id: response.id,
      unique_customer_id: response.customData?.customer_unique_id,
      eh: response.trackingHash,
      ...(config.tracking.sendPlaintextEmail && { email: payload.email })
    });
  };

  const register = async (payload: {
    gender: string;
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    hasSubscribedNewsletter: boolean;
    source: string;
    birthDate?: string;
    phoneNumber?: string;
  }) => {
    const response = await callApi({ endpoint: '/customer', payload, flow: 'legacy' });
    pushTrackingEvent({
      event: 'sign_up',
      content_name: document.location.pathname,
      page_type: 'checkout:modal_sign_up',
      page_type_id: '',
      method: 'email',
      status: 'successful',
      customer_id: response.id,
      unique_customer_id: response.customData?.customer_unique_id,
      eh: response.trackingHash,
      ...(config.tracking.sendPlaintextEmail && { email: payload.email })
    });
  };

  const initialValues = {
    type: initialType,
    gender: 'f',
    firstName: '',
    lastName: '',
    email: returningCustomer?.email ?? '',
    password: '',
    isNewsletterSignUpAccepted: false,
    source: '',
    publicKey: '',
    emailHint: '',
    signInWithCustomerNumber: 'inactive',
    ...getInitialDynamicFieldValues(undefined, 'register', 'guest')
  };

  const onSubmit = async ({
    type,
    gender,
    firstName,
    lastName,
    email,
    password,
    isNewsletterSignUpAccepted,
    source,
    birthDate,
    phoneNumber,
    publicKey,
    signInWithCustomerNumber
  }: typeof initialValues) => {
    try {
      if (type === 'signIn') {
        if (signInWithCustomerNumber === 'setPassword') {
          await setPasswordForCustomerNumber({ birthDate, email, password, publicKey });
          showNotification({
            level: 'success',
            text: formatMessage({ id: 'auth.options.signIn.notifications.signedInWithCustomerNumber' }),
            autoClose: false,
            closable: true
          });
        } else {
          await signIn({ email, password });
        }
      } else if (type === 'register') {
        await register({
          gender,
          firstName,
          lastName,
          email,
          password,
          hasSubscribedNewsletter: isNewsletterSignUpAccepted,
          source,
          ...(birthDate && { birthDate }),
          ...(phoneNumber && { phone: phoneNumber })
        });
      }
      window.location.href = '/oauth/success';
    } catch (e: any) {
      const errorI18nKey = {
        signIn: {
          404: 'auth.notifications.notFound',
          405: 'auth.notifications.deactivated',
          422: 'auth.notifications.notFound'
        },
        register: { 409: 'auth.notifications.alreadyExists' }
      }[type]?.[e.statusCode];
      if (errorI18nKey) {
        showNotification({
          level: 'error',
          text: formatMessage({ id: errorI18nKey }),
          autoClose: true,
          closable: true
        });
        pushErrorTrackingEventFromI18nKey(errorI18nKey);
      } else {
        console.error(e);
        showDefaultError();
        pushErrorTrackingEventFromErrorObject(e, snakeCase(type));
      }
    }
  };

  return (
    <div className="embedded-auth">
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <AuthForm returningCustomer={returningCustomer} isEmbeddedAuth />
      </Formik>
    </div>
  );
};

export default EmbeddedAuth;
